import { useState, useEffect, useRef } from 'react';
import { Music, Play, Pause, User, Instagram, Mail, Mic2, Upload, Send, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewArtists = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchApprovedSubmissions();
  }, []);

  const fetchApprovedSubmissions = async () => {
    try {
      const response = await axios.get(`${API_URL}/submissions/approved`);
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (submission) => {
    if (playingId === submission.id) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      // Handle both full URLs and relative paths
      let audioUrl = submission.audio_url;
      if (!audioUrl.startsWith('http')) {
        // Convert /uploads/... to /api/uploads/... for proper routing
        if (audioUrl.startsWith('/uploads/')) {
          audioUrl = `/api${audioUrl}`;
        }
        audioUrl = `${process.env.REACT_APP_BACKEND_URL}${audioUrl}`;
      }
      audioRef.current = new Audio(audioUrl);
      audioRef.current.play();
      audioRef.current.onended = () => setPlayingId(null);
      setPlayingId(submission.id);
    }
  };

  return (
    <div className="min-h-screen py-12 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D9F99D]/10 text-[#D9F99D] mb-4">
            <Mic2 className="w-4 h-4" />
            <span className="text-sm font-medium">Discover New Talent</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            New <span className="text-[#D9F99D]">Artists</span>
          </h1>
          <p className="text-zinc-400 max-w-2xl mx-auto mb-8">
            Discover fresh talent handpicked by DJ Shadow. These unsigned artists are bringing 
            fire to the underground scene.
          </p>
          <Button 
            onClick={() => setShowSubmitModal(true)}
            className="bg-[#D9F99D] text-black hover:bg-[#BEF264] rounded-full px-6 py-6 font-bold"
            data-testid="submit-music-btn"
          >
            <Upload className="w-5 h-5 mr-2" />
            Submit Your Music
          </Button>
        </div>

        {/* Submissions Grid */}
        {loading ? (
          <div className="text-center py-20 text-zinc-400">Loading...</div>
        ) : submissions.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {submissions.map((submission) => (
              <div 
                key={submission.id} 
                className="glass-card rounded-2xl p-6 hover:border-[#D9F99D]/30 transition-all"
                data-testid={`submission-${submission.id}`}
              >
                {/* Artist Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D9F99D] to-[#84CC16] flex items-center justify-center">
                    <User className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{submission.artist_name}</h3>
                    {submission.genre && (
                      <span className="text-xs text-zinc-500">{submission.genre}</span>
                    )}
                  </div>
                </div>

                {/* Track Info */}
                <div className="mb-4">
                  <h4 className="font-medium text-[#D9F99D] mb-2">{submission.track_title}</h4>
                  {submission.bio && (
                    <p className="text-sm text-zinc-400 line-clamp-3">{submission.bio}</p>
                  )}
                </div>

                {/* Play Button */}
                <Button
                  onClick={() => handlePlay(submission)}
                  className={`w-full rounded-xl py-6 font-bold transition-all ${
                    playingId === submission.id 
                      ? 'bg-[#D9F99D] text-black' 
                      : 'bg-white/5 hover:bg-white/10 text-white'
                  }`}
                  data-testid={`play-btn-${submission.id}`}
                >
                  {playingId === submission.id ? (
                    <>
                      <Pause className="w-5 h-5 mr-2" />
                      Playing...
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5 mr-2" />
                      Play Track
                    </>
                  )}
                </Button>

                {/* Social Link */}
                {submission.social_media && (
                  <a 
                    href={submission.social_media.startsWith('http') ? submission.social_media : `https://${submission.social_media}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 mt-4 text-sm text-zinc-400 hover:text-[#D9F99D] transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Follow Artist
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="glass-card rounded-2xl p-12 text-center">
            <Music className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">No Artists Yet</h3>
            <p className="text-zinc-400 mb-6">
              Be the first to get your music featured! Submit your track and DJ Shadow will review it.
            </p>
            <Button 
              onClick={() => setShowSubmitModal(true)}
              className="bg-[#D9F99D] text-black hover:bg-[#BEF264] rounded-full px-6"
            >
              <Upload className="w-4 h-4 mr-2" />
              Submit Your Music
            </Button>
          </div>
        )}
      </div>

      {/* Submit Modal */}
      <SubmitMusicModal 
        isOpen={showSubmitModal} 
        onClose={() => setShowSubmitModal(false)}
        onSuccess={() => {
          setShowSubmitModal(false);
          fetchApprovedSubmissions();
        }}
      />
    </div>
  );
};

// Submit Music Modal Component
const SubmitMusicModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    artist_name: '',
    track_title: '',
    email: '',
    genre: '',
    social_media: '',
    bio: ''
  });
  const [audioFile, setAudioFile] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.artist_name || !formData.track_title || !formData.email || !audioFile) {
      toast.error('Please fill in all required fields and upload your track');
      return;
    }

    setLoading(true);
    
    try {
      const submitData = new FormData();
      submitData.append('artist_name', formData.artist_name);
      submitData.append('track_title', formData.track_title);
      submitData.append('email', formData.email);
      submitData.append('genre', formData.genre || '');
      submitData.append('social_media', formData.social_media || '');
      submitData.append('bio', formData.bio || '');
      submitData.append('audio_file', audioFile);

      await axios.post(`${API_URL}/submissions`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('🎵 Your music has been submitted! DJ Shadow will review it soon.');
      setFormData({
        artist_name: '',
        track_title: '',
        email: '',
        genre: '',
        social_media: '',
        bio: ''
      });
      setAudioFile(null);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#18181B] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Mic2 className="w-6 h-6 text-[#D9F99D]" />
            Submit Your Music
          </DialogTitle>
        </DialogHeader>
        
        <p className="text-zinc-400 text-sm">
          Get your music heard by DJ Shadow and featured on the app. Fill out the form below and upload your best track!
        </p>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Artist Name *</Label>
              <Input
                value={formData.artist_name}
                onChange={(e) => setFormData({ ...formData, artist_name: e.target.value })}
                placeholder="Your artist name"
                className="bg-black/20 border-white/10 focus:border-[#D9F99D]"
                data-testid="artist-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Track Title *</Label>
              <Input
                value={formData.track_title}
                onChange={(e) => setFormData({ ...formData, track_title: e.target.value })}
                placeholder="Name of your track"
                className="bg-black/20 border-white/10 focus:border-[#D9F99D]"
                data-testid="track-title-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                className="bg-black/20 border-white/10 focus:border-[#D9F99D]"
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Genre</Label>
              <Input
                value={formData.genre}
                onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                placeholder="e.g., Hip Hop, Latin, R&B"
                className="bg-black/20 border-white/10 focus:border-[#D9F99D]"
                data-testid="genre-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Social Media Link</Label>
            <Input
              value={formData.social_media}
              onChange={(e) => setFormData({ ...formData, social_media: e.target.value })}
              placeholder="https://instagram.com/yourhandle"
              className="bg-black/20 border-white/10 focus:border-[#D9F99D]"
              data-testid="social-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Bio / Description</Label>
            <Textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="Tell us about yourself and your music..."
              className="bg-black/20 border-white/10 focus:border-[#D9F99D] min-h-[100px]"
              data-testid="bio-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Upload Track * (MP3, WAV, M4A)</Label>
            <div className="border-2 border-dashed border-white/10 rounded-xl p-6 text-center hover:border-[#D9F99D]/50 transition-colors">
              <input
                type="file"
                accept=".mp3,.wav,.m4a,.ogg,.flac"
                onChange={(e) => setAudioFile(e.target.files[0])}
                className="hidden"
                id="audio-upload"
                data-testid="audio-file-input"
              />
              <label htmlFor="audio-upload" className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto mb-2 text-zinc-400" />
                {audioFile ? (
                  <p className="text-[#D9F99D] font-medium">{audioFile.name}</p>
                ) : (
                  <p className="text-zinc-400">Click to upload your track</p>
                )}
              </label>
            </div>
          </div>

          <Button 
            type="submit"
            disabled={loading}
            className="w-full bg-[#D9F99D] text-black hover:bg-[#BEF264] rounded-full py-6 font-bold"
            data-testid="submit-btn"
          >
            {loading ? (
              'Submitting...'
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Submit for Review
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewArtists;
